import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Star, CaretLeft, CaretRight, SealCheck } from "@phosphor-icons/react";

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/place/KosMedico+Medizinische+%C3%84sthetik";

/** Multicolor Google "G" logo (SVG) */
const GoogleG = ({ size = 22, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    width={size}
    height={size}
    className={className}
    aria-hidden="true"
  >
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

/** Initial avatar with deterministic sage palette */
const Avatar = ({ name }) => {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const palette = ["#A7CA99", "#7E9B73", "#C8B88A", "#9CB89A", "#B7A7C8", "#8FA890"];
  const idx = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
  const bg = palette[idx];
  return (
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center text-white font-serif text-base shrink-0"
      style={{ backgroundColor: bg }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
};

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-[2px]">
    {["s1", "s2", "s3", "s4", "s5"].map((id, i) => (
      <Star
        key={id}
        size={16}
        weight="fill"
        className={i < n ? "text-[#FBBC04]" : "text-[#E4E2DA]"}
      />
    ))}
  </div>
);

const ReviewCard = ({ r, i, readMoreLabel }) => (
  <article
    data-testid={`review-card-${i}`}
    className="snap-start shrink-0 w-[280px] sm:w-[300px] lg:w-[320px] bg-[#F6F5F2] border border-[#E4E2DA] rounded-sm p-6 flex flex-col"
  >
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={r.name} />
        <div className="min-w-0">
          <div className="font-medium text-[#3E5239] text-sm truncate">{r.name}</div>
          <div className="text-[11px] text-[#9AA395] font-light tracking-wide mt-0.5">{r.date}</div>
        </div>
      </div>
      <GoogleG size={20} className="shrink-0" />
    </div>

    <div className="flex items-center gap-2 mb-4">
      <Stars n={r.rating} />
      <SealCheck size={14} weight="fill" className="text-[#1A73E8]" />
    </div>

    <p className="text-[13.5px] text-[#3E5239] font-light leading-relaxed line-clamp-5 flex-1">
      {r.text}
    </p>

    <a
      href={GOOGLE_MAPS_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`review-readmore-${i}`}
      className="mt-5 text-[13px] text-[#6B7A66] hover:text-[#3E5239] transition-colors self-start"
    >
      {readMoreLabel}
    </a>
  </article>
);

export const Reviews = () => {
  const { t } = useTranslation();
  const items = t("reviews.items", { returnObjects: true }) || [];
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateNav = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateNav();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", updateNav);
    return () => {
      el.removeEventListener("scroll", updateNav);
      window.removeEventListener("resize", updateNav);
    };
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("article");
    const delta = (card ? card.offsetWidth + 16 : 320) * dir;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const avgRating = (
    items.reduce((s, r) => s + (r.rating || 0), 0) / Math.max(items.length, 1)
  ).toFixed(1);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-24 lg:py-32 bg-[#EFEDE6] border-y border-[#E4E2DA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* LEFT — Trust summary */}
          <aside className="lg:col-span-3 flex flex-col items-center lg:items-start text-center lg:text-left">
            <div className="text-[11px] tracking-[0.32em] font-semibold text-[#3E5239] uppercase mb-3">
              {t("reviews.label", "Excelente")}
            </div>
            <div className="flex items-center gap-[3px] mb-3">
              {["m1", "m2", "m3", "m4", "m5"].map((id) => (
                <Star key={id} size={26} weight="fill" className="text-[#FBBC04]" />
              ))}
            </div>
            <div className="text-sm text-[#3E5239] font-light mb-5">
              {t("reviews.based_on", { count: items.length, defaultValue: "Com base em {{count}} avaliações" })}
              <span className="ml-2 text-[#6B7A66]">· {avgRating} / 5</span>
            </div>
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-link"
              className="inline-flex items-center gap-2 group"
              aria-label="Google"
            >
              <GoogleG size={56} />
            </a>
            <p className="mt-6 text-xs text-[#9AA395] font-light leading-relaxed max-w-[220px] hidden lg:block">
              {t("reviews.intro_short", { defaultValue: "Avaliações verificadas no Google Maps." })}
            </p>
          </aside>

          {/* RIGHT — Carousel */}
          <div className="lg:col-span-9 relative min-w-0 w-full">
            <div
              ref={scrollerRef}
              data-testid="reviews-scroller"
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1"
              style={{ scrollbarWidth: "none" }}
            >
              {items.map((r, i) => (
                <ReviewCard
                  key={`${r.name}-${r.date}`}
                  r={r}
                  i={i}
                  readMoreLabel={t("reviews.read_more", "Leia mais")}
                />
              ))}
            </div>

            {/* nav buttons */}
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={!canPrev}
              data-testid="reviews-prev"
              aria-label="previous"
              className={`hidden md:flex absolute top-1/2 -left-4 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#E4E2DA] items-center justify-center shadow-md transition-opacity ${canPrev ? "opacity-100 hover:bg-[#F6F5F2]" : "opacity-0 pointer-events-none"}`}
            >
              <CaretLeft size={16} weight="bold" className="text-[#3E5239]" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={!canNext}
              data-testid="reviews-next"
              aria-label="next"
              className={`hidden md:flex absolute top-1/2 -right-4 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#E4E2DA] items-center justify-center shadow-md transition-opacity ${canNext ? "opacity-100 hover:bg-[#F6F5F2]" : "opacity-0 pointer-events-none"}`}
            >
              <CaretRight size={16} weight="bold" className="text-[#3E5239]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
